/**
 * 使用 Mozilla PDF.js 将 PDF 逐页渲染为 Canvas。
 *
 * pdf.js 文件通过 public/vendor/pdfjs/ 静态资源提供，避免在 pnpm workspace
 * 中引入 pdfjs-dist npm 依赖时遇到的 postinstall/符号链接问题。
 *
 * 相比 iframe 内嵌原生 PDF viewer：
 * - 绕过微信内置浏览器「即将离开微信」的拦截
 * - 不触发 Chrome「正在从 PDF 文件中提取文本…」的自带提示
 * - 统一图片/Canvas 交互，和移动端图片预览体验一致
 */
import { useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';

interface PdfPreviewProps {
  url: string;
  name?: string;
  /** 外层容器背景色，用于错误/加载状态的视觉统一 */
  bgColor?: string;
}

interface PdfJsLib {
  getDocument: (src: string | { url: string }) => { promise: Promise<PdfDocument> };
  GlobalWorkerOptions: { workerSrc: string };
}

interface PdfDocument {
  numPages: number;
  getPage: (pageNumber: number) => Promise<PdfPage>;
}

interface PdfPage {
  getViewport: (opts: { scale: number }) => { width: number; height: number };
  render: (opts: { canvasContext: CanvasRenderingContext2D; viewport: { width: number; height: number } }) => { promise: Promise<void> };
}

let pdfjsPromise: Promise<PdfJsLib> | null = null;

function loadPdfJs(): Promise<PdfJsLib> {
  if (pdfjsPromise) return pdfjsPromise;

  pdfjsPromise = new Promise((resolve, reject) => {
    // 通过 <script> 加载 public/ 下的 JS，避免 Vite import-analysis
    // 报错「Cannot import non-asset file inside /public」
    const existing = document.querySelector('script[data-pdfjs]') as HTMLScriptElement | null;
    if (existing?.dataset?.ready === 'true') {
      resolve((window as unknown as { pdfjsLib: PdfJsLib }).pdfjsLib);
      return;
    }

    const script = document.createElement('script');
    script.src = '/vendor/pdfjs/pdf.min.mjs';
    script.type = 'module';
    script.dataset.pdfjs = 'true';
    script.onload = () => {
      script.dataset.ready = 'true';
      const pdfjs = (window as unknown as { pdfjsLib?: PdfJsLib }).pdfjsLib;
      if (!pdfjs) {
        reject(new Error('pdf.js 加载失败：未在 window 上找到 pdfjsLib'));
        return;
      }
      pdfjs.GlobalWorkerOptions.workerSrc = '/vendor/pdfjs/pdf.worker.min.mjs';
      resolve(pdfjs);
    };
    script.onerror = () => reject(new Error('pdf.js 脚本加载失败'));
    document.head.appendChild(script);
  });

  return pdfjsPromise;
}

export default function PdfPreview({ url, name, bgColor = '#fff' }: PdfPreviewProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [numPages, setNumPages] = useState(0);
  const renderedRef = useRef(false);

  useEffect(() => {
    if (renderedRef.current || !url || !containerRef.current) return;
    renderedRef.current = true;

    const container = containerRef.current;
    let cancelled = false;
    const canvases: HTMLCanvasElement[] = [];

    const render = async () => {
      try {
        setLoading(true);
        setError(null);

        const pdfjs = await loadPdfJs();
        if (cancelled) return;

        const pdf = await pdfjs.getDocument(url).promise;
        if (cancelled) return;

        setNumPages(pdf.numPages);

        const pageWidth = container.clientWidth || window.innerWidth - 32;
        const firstPage = await pdf.getPage(1);
        const baseViewport = firstPage.getViewport({ scale: 1 });
        const scale = Math.max(0.5, Math.min(3, pageWidth / baseViewport.width));

        for (let i = 1; i <= pdf.numPages; i++) {
          if (cancelled) return;
          const page = await pdf.getPage(i);
          const viewport = page.getViewport({ scale });
          const canvas = document.createElement('canvas');
          canvas.style.width = '100%';
          canvas.style.height = 'auto';
          canvas.style.display = 'block';
          canvas.style.marginBottom = '8px';
          canvas.width = Math.floor(viewport.width);
          canvas.height = Math.floor(viewport.height);

          const ctx = canvas.getContext('2d');
          if (!ctx) continue;

          await page.render({ canvasContext: ctx, viewport }).promise;
          canvases.push(canvas);
          container.appendChild(canvas);
        }
      } catch (err) {
        console.error('PDF 渲染失败', err);
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : String(err);
        if (msg.includes('Failed to fetch') || msg.includes('NetworkError') || msg.includes('CORS')) {
          setError('无法加载 PDF：跨域或网络受限。可点击右上角「下载」后用浏览器打开。');
        } else if (msg.includes('Missing PDF file') || msg.includes('Invalid PDF')) {
          setError('PDF 文件无效或已损坏。');
        } else {
          setError(`PDF 加载失败：${msg}`);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    render();

    return () => {
      cancelled = true;
      canvases.forEach((c) => c.remove());
      renderedRef.current = false;
    };
  }, [url]);

  const safeName = name || 'document.pdf';

  return (
    <Box
      ref={containerRef}
      sx={{
        width: '100%',
        minHeight: '100%',
        bgcolor: bgColor,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: loading || error ? 'center' : 'flex-start',
        p: 1,
        position: 'relative',
      }}
    >
      {loading && (
        <Box sx={{ textAlign: 'center', color: '#fff' }}>
          <CircularProgress color="inherit" />
          <Typography sx={{ mt: 1, fontSize: '13px' }}>
            {numPages > 0 ? `正在渲染第 ${numPages} 页…` : '正在加载 PDF…'}
          </Typography>
        </Box>
      )}

      {error && (
        <Box sx={{ textAlign: 'center', color: '#fff', px: 2 }}>
          <Typography sx={{ fontSize: '14px', mb: 2 }}>{error}</Typography>
          <Button
            variant="outlined"
            color="inherit"
            component="a"
            href={url}
            download={safeName}
            sx={{ textTransform: 'none' }}
          >
            下载后用浏览器打开
          </Button>
        </Box>
      )}
    </Box>
  );
}
